import { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Col, Form, Modal, Row, Spinner, Table } from 'react-bootstrap';
import axios from 'axios';
import { toast } from 'react-toastify';

import DateInput from './DateInput';
export const NOTE_REASONS = {
  SALES_RETURN: 'Sales return',
  POST_SALE_DISCOUNT: 'Post-sale discount',
  DEFICIENCY_IN_SERVICES: 'Deficiency in services',
  CORRECTION_IN_INVOICE: 'Correction in invoice',
  OTHERS: 'Others'
};
const CREDIT_REASONS = ['SALES_RETURN', 'POST_SALE_DISCOUNT', 'DEFICIENCY_IN_SERVICES', 'CORRECTION_IN_INVOICE', 'OTHERS'];
const DEBIT_REASONS = ['CORRECTION_IN_INVOICE', 'OTHERS'];

const num = (v) => {
  const n = parseFloat(v);
  return Number.isNaN(n) ? 0 : n;
};
const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;
// Bills issued before the rate was snapshotted on the line still carry it on the tax rate record
const lineRate = (line) => num(line.taxRatePercent ?? line.taxRate?.ratePercent);

/**
 * Issues a credit or debit note against an issued sale bill. The server is the authority on amounts
 * (it applies the bill's rate and supply type and caps what can still be credited); the figures
 * shown here are an estimate to help fill the form in.
 */
function SaleNoteModal({ show, bill, noteType, currency = '', onHide, onIssued }) {
  const isCredit = noteType === 'CREDIT';
  const [fullBill, setFullBill] = useState(null);
  const [priorNotes, setPriorNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [reason, setReason] = useState(isCredit ? 'SALES_RETURN' : 'CORRECTION_IN_INVOICE');
  const [noteDate, setNoteDate] = useState(''); // blank means today, as the server sees it
  const [remarks, setRemarks] = useState('');
  const [restock, setRestock] = useState(true);
  const [entries, setEntries] = useState({});

  useEffect(() => {
    if (!show || !bill?.id) return;
    let cancelled = false;
    setLoading(true);
    setEntries({});
    setRemarks('');
    setReason(isCredit ? 'SALES_RETURN' : 'CORRECTION_IN_INVOICE');
    setRestock(true);
    setNoteDate('');
    Promise.all([axios.get(`/api/sale-bills/${bill.id}`), axios.get(`/api/sale-notes/bill/${bill.id}`)])
      .then(([billRes, notesRes]) => {
        if (cancelled) return;
        setFullBill(billRes.data);
        setPriorNotes(Array.isArray(notesRes.data) ? notesRes.data : []);
      })
      .catch(() => {
        if (!cancelled) toast.error('Could not load the bill');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [show, bill?.id, isCredit]);

  const credited = useMemo(() => {
    const byLine = {};
    priorNotes.filter((n) => n.noteType === 'CREDIT').forEach((n) => {
      (n.lines || []).forEach((l) => {
        const cur = byLine[l.saleBillLineItemId] || { quantity: 0, taxable: 0 };
        cur.quantity += num(l.quantity);
        cur.taxable += num(l.taxableAmount);
        byLine[l.saleBillLineItemId] = cur;
      });
    });
    return byLine;
  }, [priorNotes]);

  const lines = fullBill?.lineItems || [];

  const estimate = (line) => {
    const entry = entries[line.id] || {};
    const qty = num(entry.quantity);
    const amount = num(entry.amount);
    let taxable = 0;
    if (amount > 0) taxable = amount;
    else if (isCredit && qty > 0 && num(line.quantity) > 0) taxable = (qty * num(line.taxableAmount)) / num(line.quantity);
    taxable = round2(taxable);
    const tax = round2((taxable * lineRate(line)) / 100);
    return { taxable, tax };
  };

  const totals = lines.reduce((acc, l) => {
    const e = estimate(l);
    return { taxable: acc.taxable + e.taxable, tax: acc.tax + e.tax };
  }, { taxable: 0, tax: 0 });

  const setEntry = (id, field, value) => setEntries((prev) => ({ ...prev, [id]: { ...prev[id], [field]: value } }));

  const submit = async () => {
    const payloadLines = lines
      .map((l) => {
        const e = entries[l.id] || {};
        return {
          saleBillLineItemId: l.id,
          quantity: isCredit && num(e.quantity) > 0 ? num(e.quantity) : null,
          taxableAmount: num(e.amount) > 0 ? num(e.amount) : null
        };
      })
      .filter((l) => l.quantity || l.taxableAmount);
    if (payloadLines.length === 0) {
      toast.error(isCredit ? 'Enter a returned quantity or a credit amount on at least one line' : 'Enter an amount on at least one line');
      return;
    }
    try {
      setSaving(true);
      const response = await axios.post('/api/sale-notes', {
        noteType,
        saleBillId: bill.id,
        noteDate: noteDate || null,
        reason,
        notes: remarks,
        restock: isCredit && restock && payloadLines.some((l) => l.quantity),
        lines: payloadLines
      });
      toast.success(`${isCredit ? 'Credit' : 'Debit'} note ${response.data.noteNumber} issued`);
      onIssued && onIssued(response.data);
      onHide();
    } catch (error) {
      const data = error.response?.data;
      toast.error(typeof data === 'string' && data ? data : 'Failed to issue the note');
    } finally {
      setSaving(false);
    }
  };

  const reasons = isCredit ? CREDIT_REASONS : DEBIT_REASONS;
  const hasReturnQty = lines.some((l) => num((entries[l.id] || {}).quantity) > 0);

  return (
    <Modal show={show} onHide={onHide} size="xl" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>
          {isCredit ? 'Issue credit note' : 'Issue debit note'}
          {bill ? ` — ${bill.billNumber}` : ''}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading || !fullBill ? (
          <div className="d-flex justify-content-center"><Spinner animation="border" /></div>
        ) : (
          <>
            <Alert variant="info" className="py-2 small">
              {isCredit
                ? 'A credit note reduces what the customer owes for this bill and reverses the sale and GST. Issued notes can’t be edited; if you make a mistake, issue a debit note to correct it.'
                : 'A debit note adds to what the customer owes for this bill (for example a price correction), with GST at the bill’s rate. Issued notes can’t be edited.'}
            </Alert>
            <Row className="g-3 mb-3">
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Reason</Form.Label>
                  <Form.Select value={reason} onChange={(e) => setReason(e.target.value)}>
                    {reasons.map((r) => <option key={r} value={r}>{NOTE_REASONS[r]}</option>)}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Note date (blank = today)</Form.Label>
                  <DateInput value={noteDate} min={fullBill.billDate}
                    onChange={(e) => setNoteDate(e.target.value)} />
                </Form.Group>
              </Col>
              <Col md={5}>
                <Form.Group>
                  <Form.Label>Remarks (optional)</Form.Label>
                  <Form.Control value={remarks} maxLength={1000} onChange={(e) => setRemarks(e.target.value)} />
                </Form.Group>
              </Col>
            </Row>

            <Table bordered size="sm" responsive>
              <thead>
                <tr>
                  <th>Product</th>
                  <th className="text-end">Billed qty</th>
                  <th className="text-end">Taxable</th>
                  <th className="text-end">Tax %</th>
                  {isCredit && <th className="text-end">Already credited</th>}
                  {isCredit && <th style={{ width: 130 }}>Return qty</th>}
                  <th style={{ width: 160 }}>{isCredit ? 'Credit amount (optional)' : 'Amount to add'}</th>
                  <th className="text-end">Est. total</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((l) => {
                  const done = credited[l.id] || { quantity: 0, taxable: 0 };
                  const est = estimate(l);
                  return (
                    <tr key={l.id}>
                      <td>{l.product?.name || '-'}</td>
                      <td className="text-end">{l.quantity}</td>
                      <td className="text-end">{currency} {num(l.taxableAmount).toFixed(2)}</td>
                      <td className="text-end">{lineRate(l)}</td>
                      {isCredit && <td className="text-end">{done.quantity ? `${done.quantity} (${num(done.taxable).toFixed(2)})` : '-'}</td>}
                      {isCredit && (
                        <td>
                          <Form.Control size="sm" type="number" min="0" step="any" value={(entries[l.id] || {}).quantity || ''}
                            onChange={(e) => setEntry(l.id, 'quantity', e.target.value)} />
                        </td>
                      )}
                      <td>
                        <Form.Control size="sm" type="number" min="0" step="0.01" value={(entries[l.id] || {}).amount || ''}
                          placeholder={isCredit ? 'auto from qty' : ''}
                          onChange={(e) => setEntry(l.id, 'amount', e.target.value)} />
                      </td>
                      <td className="text-end">{est.taxable > 0 ? `${currency} ${(est.taxable + est.tax).toFixed(2)}` : '-'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>

            <div className="d-flex flex-wrap justify-content-between align-items-start gap-3">
              <div>
                {isCredit && (
                  <Form.Check
                    type="checkbox"
                    id="note-restock"
                    label="Take the returned goods back into stock"
                    checked={restock}
                    disabled={!hasReturnQty}
                    onChange={(e) => setRestock(e.target.checked)}
                  />
                )}
                {isCredit && hasReturnQty && restock && (
                  <Form.Text className="text-muted d-block">
                    The goods go back to where this bill issued them from, ready to sell again.
                  </Form.Text>
                )}
              </div>
              <div className="text-end">
                <div>Estimated taxable: <strong>{currency} {round2(totals.taxable).toFixed(2)}</strong></div>
                <div>Estimated tax: <strong>{currency} {round2(totals.tax).toFixed(2)}</strong></div>
                <div className="fs-5">Estimated note total: <strong>{currency} {round2(totals.taxable + totals.tax).toFixed(2)}</strong></div>
                <div className="text-muted small">The exact amount is worked out when the note is issued.</div>
              </div>
            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={saving}>Cancel</Button>
        <Button variant={isCredit ? 'danger' : 'primary'} onClick={submit} disabled={saving || loading || !fullBill}>
          {saving ? 'Issuing...' : `Issue ${isCredit ? 'credit' : 'debit'} note`}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default SaleNoteModal;
